import React, { useContext, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Card,
    Button,
    TextField,
    Typography,
    Divider,
    CircularProgress
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import TagIcon from '@mui/icons-material/Tag';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {StateContext} from "../../../components/context/StateProvider";
import ComprasService from "../api/compras.api";
import { lineaCompraAProducto, referenciaRegistro } from "../helpers/lineaCompra";
import Order from "../../../models/Order";
import { ListOrder, FieldLabel } from "../components";
import Utils from "../../../utils/Utils";
import SocketClient from "../../../socket/SocketClient";
import {debounce} from "@mui/material";
import {INCIDENCIA_ORIGEN, ORDER_ACTION, ORDER_TYPE} from "../../../config/constants";
import NotesService from "../../../services/notes.service";
import Toasts from "../../../components/notificacion/Toasts";
import { ALTURA_BOTON, BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";


const Purchases = () => {

    const {state, updateState} = useContext(StateContext);

    const orderRef = useRef(null);

    // El callback del socket vive desde el montaje, así que su closure congelaría el
    // estado inicial. Esta ref replica lo que hacía this.context en la clase: leer
    // siempre el valor actual. Importa sobre todo para acumular toasts sin perder los
    // anteriores.
    const estadoRef = useRef(state);
    estadoRef.current = state;

    // Debounce con instancia única: limpia espacios del número escaneado
    const handleOrderChange = useMemo(() => debounce((event) => {
        if (orderRef.current) {
            orderRef.current.value = event.target.value.replace(/\s/g, "");
        }
    }, 300), []);

    useEffect(() => {
        const socketClient = new SocketClient();
        socketClient.connect();

        // El payload es un objeto (registrar y recepcionar emiten el mismo evento)
        socketClient.receiveUpdatePurchase(async (data) => {
            if (data?.pedido === orderRef.current.value) {
                await getPurchases(updateState, false, estadoRef.current);
            }
        })

        return () => {
            handleOrderChange.clear();
            updateState({toasts: []})
            updateState({products:[]});
            updateState({order: new Order()});
            socketClient.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Gestionar evento submit
     * @param event
     * @returns {Promise<void>}
     */
    const handleSubmit = async (event) => {

        if (event) {
            event.preventDefault();
        }

        updateState({products:[]});

        if (orderRef.current.value !== "") {

            updateState({loading: true});

            await getPurchases(updateState, true, estadoRef.current);
        }
    };

    /**
     * Obtener compras en funcion de la orden
     * @param updateState
     * @param resetFilter
     * @param state
     * @returns {Promise<void>}
     */
    const getPurchases = async (updateState, resetFilter, state) => {
        const numero = orderRef.current.value;

        try {
            const lineas = await ComprasService.buscar(numero, state.year);

            let complete = 0;
            let lastUpdated = null;

            const products = await Promise.all(lineas.map(async (linea, index) => {
                const esExcluida = Utils.checkRefProduct(linea.articulo);
                let registro;

                if (esExcluida) {
                    complete += 1;
                } else {
                    const registros = await ComprasService.getRegistros(linea.numero, referenciaRegistro(linea), state.year);
                    registro = registros[0];

                    if (registro && (lastUpdated === null || lastUpdated < new Date(registro.registrado))) {
                        lastUpdated = new Date(registro.registrado);
                    }
                }

                return lineaCompraAProducto(linea, registro, esExcluida, index);
            }));

            const personas = await ComprasService.getPersonas(numero, state.year);
            const order = new Order();

            if (personas.length > 0) {
                order.reviewer = (personas[0].revisado === null ? '0' : personas[0].revisado);
                order.preparer = (personas[0].preparado === null ? '0' : personas[0].preparado);
                order.state = personas[0].estado;
            }

            order.num = numero;
            let valueFilter = '0';

            if (complete === lineas.length) {
                order.complete = true;
                valueFilter = '1';
            }

            await getNoteOrder(order, state.year);

            if (lastUpdated) {
                order.setLastUpdated(lastUpdated);
            }

            if (resetFilter) {
                updateState({filters: [{field: 'state', operator: 'equals', value: valueFilter}]})
            }

            updateState({order: order});
            updateState({products: products})
            updateState({loading: false});
        } catch (error) {
            const toasts = Toasts.generateToast(error.message || 'Error al obtener el pedido', 'error');
            updateState({toasts: [...estadoRef.current.toasts, toasts]})
            updateState({loading: false});
        }
    };

    /**
     * Obtener información incidencias
     * @param order
     * @param year
     * @returns {Promise<void>}
     */
    const getNoteOrder = async (order, year) => {
        let infoOrder = Utils.getOrder(order.num);

        const currentNote = await NotesService.getNoteByNum(infoOrder.serie, infoOrder.order, INCIDENCIA_ORIGEN.PURCHASES);

        if (currentNote && Object.keys(currentNote).length > 0) {
            order.Note = currentNote;
        }
    };

    return (
            <Box id="purchase" sx={{ width: '100%' }}>
                {/* Estructura estándar de página: Card sin padding propio y un Box por
                    sección. Al ser componente de clase no hay useMediaQuery, así que el
                    responsive va con sx { xs, md } */}
                <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                    {/* Header */}
                    <Box sx={{ p: 3, pb: 2 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                mb: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                fontSize: { xs: "1.25rem", md: "1.5rem" }
                            }}
                        >
                            <ShoppingCartIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                            Puntear pedidos
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Busca un pedido de compra para puntear la mercancía recibida
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            {/* Input de número de pedido */}
                            <Box>
                                <FieldLabel icon={TagIcon}>Número de pedido</FieldLabel>
                                <TextField
                                    type="text"
                                    id="order"
                                    name="order"
                                    fullWidth
                                    placeholder="Ej: 12345"
                                    inputRef={orderRef}
                                    onFocus={() => {
                                        orderRef.current.value = ''
                                    }}
                                    onChange={handleOrderChange}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                />
                            </Box>

                            {/* Botón de búsqueda */}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={state.loading}
                                startIcon={state.loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                            >
                                {state.loading ? 'Buscando...' : 'Buscar'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Contenido */}
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                        <ListOrder type={ORDER_TYPE.PEDIDO.valueOf()} handle={ORDER_ACTION.COMPRAS} registerAction={ComprasService.registrar} order={orderRef} preparer={false} reviewer={true} camera={false} orderStateN2={false} />
                    </Box>
                </Card>
            </Box>
    );
};

export default Purchases;
